import { createSelector } from '@reduxjs/toolkit';

import { selectProductPrice } from 'src/redux/product/selectors';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import { imageOptionBounds } from 'src/utils/imageOptionBounds';
import {
  countImagesForImageOptionLimit,
  listHttpsUrlsInImageOptionValue,
} from 'src/utils/imageOptionWireFormat';
import type { ReduxApplicationState } from '../combinedReducer';
import { getOptionsBreakdown } from './helpers/selectorHelpers';

export const selectOptions = (state: ReduxApplicationState) => state.basket.options;

// Whether the product has options at all, which is what gates the price
// breakdown and the page CTA's copy. Distinct from whether any have been
// selected, which lives in selectedOptionValues.
export const selectHasOptions = createSelector([selectOptions], (options) => options.length > 0);

export const selectIsSubmitOptionsFormPending = (state: ReduxApplicationState) =>
  state.basket.isPending;

export const selectSelectedOption = (state: ReduxApplicationState, optionId: number) =>
  state.basket.selectedOptionValues[optionId];

export const selectSelectedOptionValues = (state: ReduxApplicationState) =>
  state.basket.selectedOptionValues;

export const selectTrackedEvents = (state: ReduxApplicationState) => state.basket.trackedEvents;

export const selectQuantity = (state: ReduxApplicationState) => state.basket.quantity;

export const selectIsQuantityValid = (state: ReduxApplicationState) => state.basket.isQuantityValid;

const EMPTY_IMAGE_ERRORS: string[] = [];
const EMPTY_MAP: Record<number, string[]> = {};

export const selectImageNotFoundUrlsByOption = (state: ReduxApplicationState) =>
  state.basket.imageNotFoundUrlsByOption ?? EMPTY_MAP;

// Flagged objectUrls for an option still present in its current value, so the
// error shrinks as flagged tiles are removed. Stable empty array when none.
export const selectActiveImageErrorUrls = (
  state: ReduxApplicationState,
  optionId: number,
): string[] => {
  const flagged = selectImageNotFoundUrlsByOption(state)[optionId];
  if (!flagged || flagged.length === 0) {
    return EMPTY_IMAGE_ERRORS;
  }
  const tokens = listHttpsUrlsInImageOptionValue(
    state.basket.selectedOptionValues[optionId]?.[0]?.name,
  );
  const active = flagged.filter((url) => tokens.includes(url));
  return active.length === 0 ? EMPTY_IMAGE_ERRORS : active;
};

// We're in edit mode once the hydration thunk has resolved the `editItem` id to
// a real basket item (setting `editedItem`); until then (and for a bogus id)
// `editedItem` stays null and we're in add mode.
export const selectIsInEditMode = (state: ReduxApplicationState) =>
  state.basket.editedItem !== null;

export const selectOptionsBreakdown = createSelector(
  [selectSelectedOptionValues, selectProductPrice],
  (selections, { currency }) => getOptionsBreakdown(selections, currency),
);

export const selectSelectedOptionsTotal = createSelector(
  [selectOptionsBreakdown],
  ({ amountAdded, amountRemoved }) => amountAdded + amountRemoved,
);

// What one of this product costs once its selected options are applied.
const selectItemTotalPrice = createSelector(
  [selectSelectedOptionsTotal, selectProductPrice],
  (optionsTotalAmount, productPrice) => optionsTotalAmount + productPrice.amount,
);

// What the shopper pays: the item total across the quantity they have chosen.
export const selectTotalPrice = createSelector(
  [selectItemTotalPrice, selectQuantity],
  (itemTotalPrice, quantity) => itemTotalPrice * quantity,
);

export const selectInvalidCustomOptionValues = createSelector(
  [selectSelectedOptionValues],
  (selections) =>
    Object.values(selections || {})
      .flat()
      .filter(
        (value?: CatServiceAPIProductOptionValue) =>
          !!value?.isCustomInput && value?.customOptionKind !== 'image' && !value?.isValid,
      ),
);

export const selectOutstandingOptions = createSelector(
  [selectSelectedOptionValues, selectOptions],
  (selections, personalisationOptions) =>
    personalisationOptions.filter((opt) => {
      if (!opt.required) {
        return false;
      }
      if (opt.type === 'image') {
        const { max, min } = imageOptionBounds(opt);
        const minCount = opt.required ? Math.max(min, 1) : min;
        const n = countImagesForImageOptionLimit(selections[opt.id]?.[0]);
        return n < minCount || n > max;
      }
      return !selections[opt.id]?.length;
    }),
);

export const selectNumberOfRequiredOptions = createSelector([selectOptions], (options) =>
  options.reduce((count, opt) => (opt.required ? count + 1 : count), 0),
);

// Whether anything must be configured before the product can be bought. When nothing is, the
// shopper never has to open the options drawer, so quantity and the express wallets belong on
// the page rather than inside it.
export const selectHasRequiredOptions = createSelector(
  [selectNumberOfRequiredOptions],
  (numberOfRequiredOptions) => numberOfRequiredOptions > 0,
);

export const selectOptionIdsWithImageErrors = createSelector(
  [selectImageNotFoundUrlsByOption, selectSelectedOptionValues],
  (map, selections) =>
    Object.keys(map)
      .map(Number)
      .filter((id) => {
        const tokens = listHttpsUrlsInImageOptionValue(selections[id]?.[0]?.name);
        return (map[id] ?? []).some((url) => tokens.includes(url));
      }),
);

export const selectAreRequiredOptionsIncomplete = createSelector(
  [selectOutstandingOptions, selectInvalidCustomOptionValues, selectOptionIdsWithImageErrors],
  (outstanding, invalidCustom, imageErrorIds) =>
    outstanding.length > 0 || invalidCustom.length > 0 || imageErrorIds.length > 0,
);
